import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import SmartImage from "./SmartImage";
import { assetUrl } from "../adminApi";
import { useI18n } from "../i18n";

export default function ProjectCarousel({ projects = [] }) {
  const track = useRef(null);
  const { loc, t } = useI18n();

  const scrollBy = (d) => {
    const el = track.current;
    if (!el) return;
    el.scrollBy({ left: d * Math.min(el.clientWidth * 0.8, 720), behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="project-carousel">
      <div className="flex justify-end gap-2 mb-5">
        <button
          aria-label="Previous projects"
          onClick={() => scrollBy(-1)}
          data-testid="carousel-prev"
          className="w-10 h-10 border border-[#E2E0D8] bg-white flex items-center justify-center text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] transition-colors duration-300"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
        </button>
        <button
          aria-label="Next projects"
          onClick={() => scrollBy(1)}
          data-testid="carousel-next"
          className="w-10 h-10 border border-[#E2E0D8] bg-white flex items-center justify-center text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] transition-colors duration-300"
        >
          <ArrowRight size={14} strokeWidth={1.6} />
        </button>
      </div>

      <div
        ref={track}
        className="flex gap-5 lg:gap-7 overflow-x-auto snap-x snap-mandatory pb-3 no-scrollbar"
        style={{ scrollbarWidth: "none" }}
      >
        {projects.map((p, i) => (
          <Link
            key={p.id}
            to={`/portfolio/${p.slug}`}
            data-testid={`carousel-card-${p.slug}`}
            className="group snap-start shrink-0 w-[84%] sm:w-[54%] lg:w-[38%] bg-white border border-[#E2E0D8] hover:border-[#C8B29B] transition-colors duration-500"
          >
            <div className="media aspect-[4/3] relative overflow-hidden">
              <SmartImage
                src={assetUrl(p.images?.[0])}
                alt={loc(p.title)}
                fallback={p.fallback}
                fallbackIndex={i}
                params="w=1200&q=80&auto=format&fit=crop"
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
              />
              <span className="absolute top-4 left-4 mono text-[9px] bg-[#F7F6F2]/90 backdrop-blur-sm px-2.5 py-1.5 text-[#141414]">
                {p.category}
              </span>
            </div>
            <div className="p-6 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-[18px] lg:text-[21px] font-medium text-[#141414] leading-snug">
                  {loc(p.title)}
                </h3>
                <p className="mono text-[9.5px] text-[#6B675F] mt-2">
                  {p.location} · {p.year}
                </p>
              </div>
              <span className="mt-1 shrink-0 w-9 h-9 border border-[#E2E0D8] flex items-center justify-center text-[#141414] group-hover:bg-[#141414] group-hover:text-[#F7F6F2] group-hover:border-[#141414] transition-colors duration-400">
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </span>
            </div>
          </Link>
        ))}

        <Link
          to="/portfolio"
          className="snap-start shrink-0 w-[62%] sm:w-[34%] lg:w-[24%] border border-[#E2E0D8] bg-[#EFECE6] flex flex-col justify-end p-7 hover:bg-[#E7E3DA] transition-colors duration-500"
        >
          <ArrowUpRight size={22} strokeWidth={1.4} className="text-[#8C7862] mb-6" />
          <span className="display text-[24px] lg:text-[28px] text-[#141414] leading-tight">
            {t("cta.allProjects")}
          </span>
        </Link>
      </div>
    </div>
  );
}
